import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Crown, Sparkles, ArrowRight, X } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { redirectToCheckout } from "../../services/stripeService";
import { getUserSubscription } from "../../services/subscriptionService";
import { loginWithGoogle } from "../../app/features/auth";
import Navigation from "../../components/sections/Navigation";
import { Footer } from "../../components";
import Button from "../../components/ui/Button";
import toast from "react-hot-toast";

const Plans = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session, user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  useEffect(() => {
    document.documentElement.classList.add("dark");
    loadSubscription();
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [user]);

  const loadSubscription = async () => {
    if (!user?.id) {
      setLoadingSubscription(false);
      return;
    }

    try {
      const { subscription: sub, error } = await getUserSubscription(user.id);
      if (error) {
        console.error("Error loading subscription:", error);
      } else {
        setSubscription(sub);
      }
    } catch (error) {
      console.error("Exception loading subscription:", error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleSubscribe = async (planType) => {
    if (!session || !user) {
      toast.error("Please sign in to subscribe");
      try {
        await dispatch(loginWithGoogle()).unwrap();
      } catch (error) {
        console.error("Error signing in:", error);
        toast.error("Failed to sign in. Please try again.");
      }
      return;
    }

    const priceId =
      planType === "plan_b"
        ? import.meta.env.REACT_APP_STRIPE_PLAN_B_PRICE_ID
        : import.meta.env.REACT_APP_STRIPE_PLAN_C_PRICE_ID;

    if (!priceId) {
      toast.error("Stripe configuration error. Please contact support.");
      return;
    }

    setLoading(true);
    try {
      await redirectToCheckout(priceId, user.id);
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      toast.error(
        error?.message || "Failed to start checkout. Please try again."
      );
      setLoading(false);
    }
  };

  const plans = [
    {
      name: "Free",
      planType: "free",
      price: "$0",
      period: "forever",
      description: "Perfect for trying out our service",
      icon: <Zap className="w-6 h-6" />,
      color: "zinc",
      features: {
        "Daily Analyses": "1 analysis per 24 hours",
        "Personality Insights": "Basic personality insights",
        "Posture Analysis": "Posture analysis included",
        "Style Recommendations": "Style recommendations",
        Support: "Community support",
        "Credit Card": "No credit card required",
      },
      limitations: ["Limited to 1 analysis per day", "Basic insights only"],
    },
    {
      name: "Basic",
      planType: "plan_b",
      price: "$4",
      period: "per month",
      description: "Great for regular users",
      icon: <Crown className="w-6 h-6" />,
      color: "teal",
      popular: true,
      features: {
        "Daily Analyses": "5 analyses per 24 hours",
        "Personality Insights": "All free features",
        "Posture Analysis": "Advanced posture analysis",
        "Style Recommendations": "Enhanced style recommendations",
        Support: "Email support",
        Processing: "Priority processing",
        "Cancel Anytime": "Cancel anytime",
      },
      limitations: [],
    },
    {
      name: "Pro",
      planType: "plan_c",
      price: "$10",
      period: "per month",
      description: "For power users and professionals",
      icon: <Sparkles className="w-6 h-6" />,
      color: "rose",
      features: {
        "Daily Analyses": "Unlimited analyses",
        "Personality Insights": "All Basic features",
        "Posture Analysis": "Advanced posture analysis",
        "Style Recommendations": "Premium style recommendations",
        Support: "Priority support",
        Processing: "Fastest processing",
        "Advanced Insights": "Advanced insights & analytics",
        "Cancel Anytime": "Cancel anytime",
      },
      limitations: [],
    },
  ];

  const currentPlan = subscription?.plan_type || "free";

  const featureComparison = [
    { feature: "Daily Analyses", free: "1", planB: "5", planC: "Unlimited" },
    {
      feature: "Personality Insights",
      free: "Basic",
      planB: "Standard",
      planC: "Advanced",
    },
    { feature: "Posture Analysis", free: "✓", planB: "✓", planC: "✓" },
    {
      feature: "Style Recommendations",
      free: "Basic",
      planB: "Enhanced",
      planC: "Premium",
    },
    {
      feature: "Processing Speed",
      free: "Standard",
      planB: "Priority",
      planC: "Fastest",
    },
    {
      feature: "Support",
      free: "Community",
      planB: "Email",
      planC: "Priority",
    },
    {
      feature: "Advanced Analytics",
      free: <X className="w-4 h-4 text-red-400" />,
      planB: <X className="w-4 h-4 text-red-400" />,
      planC: <Check className="w-4 h-4 text-teal-400" />,
    },
  ];

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
      <Navigation />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 via-rose-500/10 to-orange-500/10 border border-teal-500/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
              <span className="text-xs sm:text-sm font-medium text-teal-400">
                Choose Your Plan
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
              Detailed Plan Comparison
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto">
              Compare all features and find the perfect plan for your needs
            </p>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center gap-2 p-1 rounded-lg bg-stone-900/50 border border-zinc-800">
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingPeriod === "monthly"
                    ? "bg-teal-500 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingPeriod === "annual"
                    ? "bg-teal-500 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                Annual
                <span className="ml-1 text-xs text-teal-300">Save 20%</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-16">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.planType;
              const isPopular = plan.popular;
              const colorClasses = {
                teal: "border-teal-500/50 bg-gradient-to-br from-teal-950/30 to-stone-900/50",
                rose: "border-rose-500/50 bg-gradient-to-br from-rose-950/20 to-stone-900/50",
                zinc: "border-zinc-800 bg-stone-900/30",
              };

              return (
                <div
                  key={plan.planType}
                  className={`relative rounded-2xl p-6 sm:p-8 border transition-all duration-300 ${
                    isPopular
                      ? `${colorClasses[plan.color]} scale-105`
                      : isCurrentPlan
                      ? "border-rose-500/50 bg-gradient-to-br from-rose-950/20 to-stone-900/50"
                      : colorClasses[plan.color] + " hover:border-zinc-700"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-teal-500 text-white text-xs font-semibold">
                      Most Popular
                    </div>
                  )}

                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-semibold">
                      Current
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`p-2 rounded-lg ${
                          plan.color === "teal"
                            ? "bg-teal-500/20 text-teal-400"
                            : plan.color === "rose"
                            ? "bg-rose-500/20 text-rose-400"
                            : "bg-zinc-700/50 text-zinc-400"
                        }`}
                      >
                        {plan.icon}
                      </div>
                      <h3 className="text-2xl font-bold">{plan.name}</h3>
                    </div>
                    <p className="text-zinc-400 text-sm mb-4">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-zinc-400">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {Object.entries(plan.features).map(([key, value]) => (
                      <li key={key} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <span className="text-zinc-300 font-medium">
                            {key}:
                          </span>
                          <span className="text-zinc-400 ml-2">{value}</span>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {plan.limitations.length > 0 && (
                    <div className="mb-6 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <p className="text-xs font-semibold text-zinc-400 mb-2">
                        Limitations:
                      </p>
                      <ul className="space-y-1">
                        {plan.limitations.map((limitation, idx) => (
                          <li
                            key={idx}
                            className="text-xs text-zinc-500 flex items-start gap-2"
                          >
                            <X className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {limitation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={() => handleSubscribe(plan.planType)}
                    disabled={isCurrentPlan || loading}
                    className={`w-full ${
                      isCurrentPlan
                        ? "bg-zinc-800 text-zinc-400 cursor-not-allowed"
                        : plan.color === "teal"
                        ? "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                        : plan.color === "rose"
                        ? "bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
                        : "bg-gradient-to-r from-zinc-700 to-zinc-800 hover:from-zinc-600 hover:to-zinc-700"
                    }`}
                  >
                    {isCurrentPlan ? (
                      "Current Plan"
                    ) : plan.planType === "free" ? (
                      "Get Started"
                    ) : (
                      <>
                        Subscribe Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              Feature Comparison
            </h2>
            <div className="rounded-2xl border border-zinc-800 bg-stone-900/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-4 text-zinc-300 font-semibold">
                        Feature
                      </th>
                      <th className="text-center p-4 text-zinc-300 font-semibold">
                        Free
                      </th>
                      <th className="text-center p-4 text-zinc-300 font-semibold">
                        Basic
                      </th>
                      <th className="text-center p-4 text-zinc-300 font-semibold">
                        Pro
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureComparison.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-zinc-800/50 hover:bg-zinc-900/30 transition-colors"
                      >
                        <td className="p-4 text-zinc-300 font-medium">
                          {row.feature}
                        </td>
                        <td className="p-4 text-center text-zinc-400">
                          {row.free}
                        </td>
                        <td className="p-4 text-center text-zinc-400">
                          {row.planB}
                        </td>
                        <td className="p-4 text-center text-zinc-400">
                          {row.planC}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="inline-block p-8 rounded-2xl bg-gradient-to-br from-teal-950/30 to-rose-950/30 border border-teal-500/20">
              <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
              <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                Check out our terms and conditions or contact our support team
                for more information.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/terms")}
                  variant="outline"
                  className="border-teal-500/50 hover:bg-teal-500/10"
                >
                  View Terms & Conditions
                </Button>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                >
                  View Pricing
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Plans;
